// src/v3/ui/backtester/backtester.hooks.ts
//
// Backtester server-state hooks built on TanStack Query.
// All reads go through useApiQuery, all mutations through useApiMutation.
// Query keys from the single registry (qk.backtester.*).
// No direct apiFetch/fetch calls — HTTP is handled by _common wrappers.
// View-state (sync RPC results from /run, /analyze, /compare, /optimize)
// remains in useState per  (not persisted server-state).

import { useCallback, useState } from "react";
import { z } from "zod";
import { qk, useApiMutation, useApiQuery } from "../_common";
import { STRATEGY_SOURCE, STRATEGY_SOURCE_OBJ } from "../_deps";

// ─── Schemas (type definitions; runtime validation is on the backend) ──────

const strategyInfoSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  version: z.string(),
  params: z.record(z.string(), z.unknown()).default({}),
  symbols: z.array(z.string()).default([]),
  source: z.enum(STRATEGY_SOURCE).default(STRATEGY_SOURCE_OBJ.BUILTIN),
  isActive: z.boolean().default(true),
  updatedAt: z.string().optional(),
  // REQ-8/9 (v1.1): код стратегии (только для source=user).
  code: z.string().nullable().optional(),
});

const strategiesResponseSchema = z.object({
  items: z.array(strategyInfoSchema),
});

export type StrategyInfo = z.infer<typeof strategyInfoSchema>;

// ─── Backtest run meta / detail types (persisted runs) ──────────────────────

const backtestRunMetaSchema = z.object({
  id: z.string(),
  strategyId: z.string(),
  symbols: z.array(z.string()),
  params: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});

type BacktestRunMeta = z.infer<typeof backtestRunMetaSchema>;

export interface BacktestRunsResponse {
  items: BacktestRunMeta[];
  total: number;
}

/** Persisted backtest result summary (from analyze usecase — differs from sync /run). */
const persistedBacktestSummarySchema = z.object({
  totalTrades: z.number(),
  wins: z.number(),
  losses: z.number(),
  winRate: z.number(),
  avgPnlPct: z.number(),
  medianPnlPct: z.number(),
  stdDevPnlPct: z.number(),
  sharpeLike: z.number(),
  sortinoRatio: z.number(),
  calmarRatio: z.number(),
  profitFactor: z.number(),
  maxDrawdownPct: z.number(),
  maxConsecutiveLosses: z.number(),
  avgHoldBars: z.number(),
  totalReturnPct: z.number(),
});

const persistedBenchmarkSchema = z.object({
  buyHoldReturnPct: z.number(),
  buyHoldMaxDrawdownPct: z.number(),
  buyHoldSharpe: z.number(),
  vsBenchmark: z.number(),
});

const persistedPortfolioSchema = z.object({
  equityCurve: z.array(z.object({ date: z.string(), equity: z.number() })),
  drawdown: z.array(z.object({ date: z.string(), drawdownPct: z.number() })),
});

const persistedBacktestResultSchema = z.object({
  meta: z.record(z.string(), z.unknown()),
  summary: persistedBacktestSummarySchema,
  benchmark: persistedBenchmarkSchema,
  breakdowns: z.record(z.string(), z.unknown()),
  dataQuality: z.object({
    warnings: z.array(z.string()),
    impact: z.array(z.string()),
  }),
  monteCarlo: z.record(z.string(), z.unknown()),
  portfolio: persistedPortfolioSchema,
  trades: z.array(z.record(z.string(), z.unknown())),
});

type PersistedBacktestResult = z.infer<typeof persistedBacktestResultSchema>;

export interface BacktestRunDetail extends BacktestRunMeta {
  result: PersistedBacktestResult;
}

// ─── Sync /run response schema (rawBacktestOutput — view-state) ─────────────

const backtestResponseSchema = z.object({
  netProfit: z.coerce.number().default(0),
  totalTrades: z.coerce.number().default(0),
  winRate: z.coerce.number().default(0),
  sharpe: z.coerce.number().default(0),
  maxDrawdown: z.coerce.number().default(0),
  profitFactor: z.coerce.number().default(0),
  breakdowns: z.record(z.string(), z.unknown()).default({}),
  trades: z.array(z.record(z.string(), z.unknown())).default([]),
  benchmark: z
    .object({
      buyHoldReturnPct: z.coerce.number().default(0),
      buyHoldMaxDrawdownPct: z.coerce.number().default(0),
      buyHoldSharpe: z.coerce.number().default(0),
      vsBenchmark: z.coerce.number().default(0),
    })
    .optional(),
  portfolio: z
    .object({
      equityCurve: z
        .array(z.object({ date: z.string(), equity: z.number() }))
        .optional(),
      drawdown: z
        .array(z.object({ date: z.string(), drawdownPct: z.number() }))
        .optional(),
    })
    .optional(),
  monteCarlo: z
    .array(z.object({ range: z.string(), count: z.number() }))
    .optional(),
  heatmap: z
    .object({
      cells: z.array(
        z.object({ paramX: z.string(), paramY: z.string(), value: z.number() }),
      ),
      paramXLabel: z.string(),
      paramYLabel: z.string(),
      valueLabel: z.string(),
    })
    .optional(),
  stalenessWarning: z.string().optional(),
});

const backtestSummarySchema = backtestResponseSchema.pick({
  netProfit: true,
  totalTrades: true,
  winRate: true,
  sharpe: true,
  maxDrawdown: true,
  profitFactor: true,
});

type BacktestSummary = z.infer<typeof backtestSummarySchema>;

const backtestResultSchema = z.object({
  strategy: z.string(),
  summary: backtestSummarySchema,
  breakdowns: z.record(z.string(), z.unknown()),
  trades: z.array(z.record(z.string(), z.unknown())),
  benchmark: backtestResponseSchema.shape.benchmark,
  portfolio: backtestResponseSchema.shape.portfolio,
  monteCarlo: backtestResponseSchema.shape.monteCarlo,
  heatmap: backtestResponseSchema.shape.heatmap,
  stalenessWarning: z.string().optional(),
});

type BacktestResult = z.infer<typeof backtestResultSchema>;

// ─── Strategy queries ───────────────────────────────────────────────────────

/**
 * Fetch the list of available backtester strategies.
 * Query key: qk.backtester.strategies.
 */
export function useBacktestStrategies() {
  const query = useApiQuery<z.infer<typeof strategiesResponseSchema>>(
    qk.backtester.strategies,
    "/api/backtester/strategies",
  );

  return {
    strategies: query.data?.items ?? [],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ─── Strategy mutations ─────────────────────────────────────────────────────

const createStrategyInputSchema = z.object({
  strategyId: z.string(),
  name: z.string(),
  description: z.string(),
  code: z.string(),
});

type CreateStrategyInput = z.infer<typeof createStrategyInputSchema>;

interface UpdateStrategyVars {
  id: string;
  input: {
    strategyId: string;
    name?: string;
    description?: string;
    code?: string;
    isActive?: boolean;
  };
}

interface CloneStrategyVars {
  id: string;
  newId: string;
  newName: string;
}

/**
 * Create a new user strategy (POST /api/backtester/strategies).
 * Invalidates qk.backtester.strategies on success.
 */
export function useCreateStrategy() {
  const mutation = useApiMutation<CreateStrategyInput, unknown>(
    "/api/backtester/strategies",
    "POST",
    { invalidateKeys: [qk.backtester.strategies] },
  );

  const createStrategy = useCallback(
    async (
      input: CreateStrategyInput,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        await mutation.mutateAsync(input);
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Failed to create strategy",
        };
      }
    },
    [mutation],
  );

  return { createStrategy, isPending: mutation.isPending };
}

/**
 * Update an existing user strategy (PUT /api/backtester/strategies/:id).
 * Invalidates qk.backtester.strategies on success.
 */
export function useUpdateStrategy() {
  const mutation = useApiMutation<UpdateStrategyVars, unknown>(
    (vars) => `/api/backtester/strategies/${encodeURIComponent(vars.id)}`,
    "PUT",
    {
      invalidateKeys: [qk.backtester.strategies],
      body: (vars) => vars.input,
    },
  );

  const updateStrategy = useCallback(
    async (
      id: string,
      input: UpdateStrategyVars["input"],
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        await mutation.mutateAsync({ id, input });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Failed to update strategy",
        };
      }
    },
    [mutation],
  );

  return { updateStrategy, isPending: mutation.isPending };
}

/**
 * Delete a user strategy (DELETE /api/backtester/strategies/:id).
 * Invalidates qk.backtester.strategies on success.
 */
export function useDeleteStrategy() {
  const mutation = useApiMutation<{ id: string }, unknown>(
    (vars) => `/api/backtester/strategies/${encodeURIComponent(vars.id)}`,
    "DELETE",
    { invalidateKeys: [qk.backtester.strategies] },
  );

  const deleteStrategy = useCallback(
    async (
      id: string,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        await mutation.mutateAsync({ id });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Failed to delete strategy",
        };
      }
    },
    [mutation],
  );

  return { deleteStrategy, isPending: mutation.isPending };
}

/**
 * Clone a builtin strategy (POST /api/backtester/strategies/:id/clone).
 * Invalidates qk.backtester.strategies on success.
 */
export function useCloneStrategy() {
  const mutation = useApiMutation<CloneStrategyVars, unknown>(
    (vars) => `/api/backtester/strategies/${encodeURIComponent(vars.id)}/clone`,
    "POST",
    {
      invalidateKeys: [qk.backtester.strategies],
      body: (vars) => ({ newId: vars.newId, newName: vars.newName }),
    },
  );

  const cloneStrategy = useCallback(
    async (
      id: string,
      newId: string,
      newName: string,
    ): Promise<{ ok: true } | { ok: false; error: string }> => {
      try {
        await mutation.mutateAsync({ id, newId, newName });
        return { ok: true };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Failed to clone strategy",
        };
      }
    },
    [mutation],
  );

  return { cloneStrategy, isPending: mutation.isPending };
}

/**
 * AI-generate strategy code (POST /api/backtester/strategies/generate-code).
 * No invalidation — result is not persisted in the strategies list.
 */
export function useGenerateStrategyCode() {
  const mutation = useApiMutation<{ description: string }, { code: string }>(
    "/api/backtester/strategies/generate-code",
    "POST",
  );

  const generateStrategyCode = useCallback(
    async (
      description: string,
    ): Promise<{ ok: true; code: string } | { ok: false; error: string }> => {
      try {
        const data = await mutation.mutateAsync({ description });
        return { ok: true, code: data.code };
      } catch (e) {
        return {
          ok: false,
          error: e instanceof Error ? e.message : "Failed to generate code",
        };
      }
    },
    [mutation],
  );

  return { generateStrategyCode, isPending: mutation.isPending };
}

// ─── Persisted backtest runs — queries ──────────────────────────────────────

/**
 * Fetch the list of past backtest runs (metadata, paginated).
 * Query key: qk.backtester.runs.
 */
export function useBacktestRuns(filters?: {
  strategyId?: string;
  limit?: number;
  offset?: number;
}) {
  const searchParams = new URLSearchParams();
  if (filters?.strategyId) searchParams.set("strategyId", filters.strategyId);
  searchParams.set("limit", String(filters?.limit ?? 50));
  searchParams.set("offset", String(filters?.offset ?? 0));
  const url = `/api/backtester/runs?${searchParams.toString()}`;

  const query = useApiQuery<BacktestRunsResponse>(qk.backtester.runs, url);

  return {
    runs: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

/**
 * Fetch the full detail of a single persisted backtest run (meta + result).
 * Query key: qk.backtester.runDetail(id). Disabled when id is falsy.
 */
export function useBacktestRunDetail(id: string | null) {
  const query = useApiQuery<BacktestRunDetail>(
    qk.backtester.runDetail(id ?? ""),
    `/api/backtester/runs/${encodeURIComponent(id ?? "")}`,
    { enabled: Boolean(id) },
  );

  return {
    detail: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
  };
}

// ─── Persisted backtest runs — mutations ────────────────────────────────────

/**
 * Create a persisted backtest run (POST /api/backtester/runs).
 * The backend runs the backtest, persists the result, and returns meta.
 * Invalidates qk.backtester.runs on success.
 */
export function useCreateBacktestRun() {
  return useApiMutation<
    {
      strategy: string;
      symbols: string[];
      params: Record<string, unknown>;
      useAI?: boolean;
    },
    BacktestRunMeta
  >("/api/backtester/runs", "POST", {
    invalidateKeys: [qk.backtester.runs],
  });
}

/**
 * Delete a persisted backtest run (DELETE /api/backtester/runs/:id).
 * Invalidates qk.backtester.runs on success.
 */
export function useDeleteBacktestRun() {
  return useApiMutation<{ id: string }, { id: string }>(
    (vars) => `/api/backtester/runs/${encodeURIComponent(vars.id)}`,
    "DELETE",
    { invalidateKeys: [qk.backtester.runs] },
  );
}

// ─── Sync RPC view-state (results in useState per ) ────────────────

/**
 * Sync backtest RPC hooks. Results are NOT persisted — they live in view-state
 * (useState) per . HTTP calls go through useApiMutation (no invalidation:
 * nothing to invalidate since results are not server-state).
 */
export function useBacktester() {
  const [results, setResults] = useState<BacktestResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const runMutation = useApiMutation<
    { strategy: string; params: Record<string, unknown> },
    unknown
  >("/api/backtester/run", "POST");

  const analyzeMutation = useApiMutation<
    { trades: Record<string, unknown>[] },
    unknown
  >("/api/backtester/analyze", "POST");

  const compareMutation = useApiMutation<
    { results: BacktestResult[] },
    unknown
  >("/api/backtester/compare", "POST");

  const optimizeMutation = useApiMutation<
    {
      strategy: string;
      symbols: string[];
      paramGrid: Record<string, Array<number | string>>;
      initialCapital: number;
      dateFrom?: string;
      dateTo?: string;
      sizingMode?: string;
    },
    unknown
  >("/api/backtester/optimize", "POST");

  const runTest = useCallback(
    async (strategy: string, params: Record<string, unknown>) => {
      setError(null);
      try {
        const rawOutput = await runMutation.mutateAsync({ strategy, params });
        const parsed = backtestResponseSchema.parse(rawOutput);
        const result: BacktestResult = {
          strategy,
          summary: {
            netProfit: parsed.netProfit,
            totalTrades: parsed.totalTrades,
            winRate: parsed.winRate,
            sharpe: parsed.sharpe,
            maxDrawdown: parsed.maxDrawdown,
            profitFactor: parsed.profitFactor,
          },
          breakdowns: parsed.breakdowns,
          trades: parsed.trades,
          ...(parsed.benchmark ? { benchmark: parsed.benchmark } : {}),
          ...(parsed.portfolio ? { portfolio: parsed.portfolio } : {}),
          ...(parsed.monteCarlo ? { monteCarlo: parsed.monteCarlo } : {}),
          ...(parsed.heatmap ? { heatmap: parsed.heatmap } : {}),
          ...(parsed.stalenessWarning
            ? { stalenessWarning: parsed.stalenessWarning }
            : {}),
        };
        setResults((prev) => [...prev, result]);
        return result;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to run test");
        return null;
      }
    },
    [runMutation],
  );

  const analyze = useCallback(
    async (trades: Record<string, unknown>[]) => {
      try {
        return await analyzeMutation.mutateAsync({ trades });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to analyze");
        return null;
      }
    },
    [analyzeMutation],
  );

  const compare = useCallback(
    async (resultsToCompare: BacktestResult[]) => {
      try {
        return await compareMutation.mutateAsync({ results: resultsToCompare });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to compare");
        return null;
      }
    },
    [compareMutation],
  );

  const optimize = useCallback(
    async (params: {
      strategy: string;
      symbols: string[];
      paramGrid: Record<string, Array<number | string>>;
      initialCapital: number;
      dateFrom?: string;
      dateTo?: string;
      sizingMode?: string;
    }) => {
      setError(null);
      try {
        return await optimizeMutation.mutateAsync(params);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to optimize");
        return null;
      }
    },
    [optimizeMutation],
  );

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  return {
    results,
    isLoading: runMutation.isPending || optimizeMutation.isPending,
    error,
    runTest,
    analyze,
    compare,
    optimize,
    clearResults,
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Map a persisted backtest run detail (from GET /api/backtester/runs/:id)
 * to the view-state BacktestResult shape that ResultsViewer expects.
 * The persisted summary uses different field names (sharpeLike, maxDrawdownPct,
 * totalReturnPct) than the sync /run response (sharpe, maxDrawdown, netProfit).
 */
export function mapDetailToViewResult(
  detail: BacktestRunDetail,
): BacktestResult {
  const s = detail.result.summary;
  const initialCapitalValue = detail.params["initialCapital"];
  const initialCapital =
    typeof initialCapitalValue === "number" ? initialCapitalValue : 10000;
  return {
    strategy: detail.strategyId,
    summary: {
      netProfit: (s.totalReturnPct / 100) * initialCapital,
      totalTrades: s.totalTrades,
      winRate: s.winRate,
      sharpe: s.sharpeLike,
      maxDrawdown: s.maxDrawdownPct,
      profitFactor: s.profitFactor,
    },
    breakdowns: detail.result.breakdowns,
    trades: detail.result.trades,
    ...(detail.result.benchmark ? { benchmark: detail.result.benchmark } : {}),
    ...(detail.result.portfolio ? { portfolio: detail.result.portfolio } : {}),
  };
}

export type { BacktestResult, BacktestSummary };
